/** FNV-1a over the source id, so a source draws the same mark on every page. */
const hashId = (id: string): number => {
  let h = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const HUES = ['--c-blue', '--c-green', '--c-violet', '--c-amber', '--c-pink', '--c-teal'];

/** One quadrant, drawn in a 1 by 1 cell and turned by `turn` quarters. */
const Shape = ({ kind, turn, fill }: { kind: number; turn: number; fill: string }) => {
  const rotate = `rotate(${turn * 90} 0.5 0.5)`;
  if (kind === 0) return <path d="M0 0 H1 A1 1 0 0 1 0 1 Z" fill={fill} transform={rotate} />;
  if (kind === 1) return <path d="M0 0 H1 L0 1 Z" fill={fill} transform={rotate} />;
  if (kind === 2) return <circle cx={0.5} cy={0.5} r={0.36} fill={fill} />;
  return <path d="M0 0.5 A0.5 0.5 0 0 1 1 0.5 Z" fill={fill} transform={rotate} />;
};

export interface SourceMarkProps {
  /** the source's stable public id, e.g. `anonymous-quokka` */
  id: string;
  size: number;
  className?: string;
}

/**
 * The mark of an anonymous source: four quadrants of quarter circles,
 * triangles and discs in two category hues, all chosen by the source id.
 */
export const SourceMark = ({ id, size, className }: SourceMarkProps) => {
  const h = hashId(id);
  const first = h % HUES.length;
  const second = (first + 1 + ((h >>> 8) % (HUES.length - 1))) % HUES.length;
  const hues = [`var(${HUES[first]})`, `var(${HUES[second]})`];
  const shapes = hashId(`${id}:bauhaus`);
  return (
    <span
      aria-hidden
      className={`grid shrink-0 place-items-center border border-line bg-surface-2${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.24) }}
    >
      <svg aria-hidden="true" viewBox="0 0 2 2" width={size * 0.64} height={size * 0.64}>
        {[0, 1, 2, 3].map((q) => (
          <g key={q} transform={`translate(${q % 2} ${Math.floor(q / 2)})`}>
            <Shape
              kind={(shapes >>> (q * 4)) % 4}
              turn={(shapes >>> (q * 4 + 2)) % 4}
              fill={hues[q % 3 === 0 ? 0 : 1]}
            />
          </g>
        ))}
      </svg>
    </span>
  );
};
